import React, { useEffect } from 'react'
import { IconSolid } from '../presentation-ui/Icon'
import { cn } from '../../lib/utils'

export interface PromptModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  prompt: string
  description?: string
  tags?: string[]
}

export interface ModalTriggerButtonProps {
  onClick: () => void
  icon: string
  text: string
  theme?: 'blue' | 'green' | 'purple' | 'orange' | 'red'
  className?: string
}

const themeStyles: Record<string, string> = {
  blue: 'bg-blue-600 hover:bg-blue-700 text-white',
  green: 'bg-green-600 hover:bg-green-700 text-white',
  purple: 'bg-purple-600 hover:bg-purple-700 text-white',
  orange: 'bg-orange-600 hover:bg-orange-700 text-white',
  red: 'bg-red-600 hover:bg-red-700 text-white',
}

export const ModalTriggerButton: React.FC<ModalTriggerButtonProps> = ({
  onClick,
  icon,
  text,
  theme = 'blue',
  className,
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
        themeStyles[theme],
        className
      )}
    >
      <IconSolid name={icon} size="sm" />
      <span>{text}</span>
    </button>
  )
}

export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  title,
  prompt,
  description,
  tags,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <h2 className="text-2xl font-bold text-foreground">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-muted transition-colors"
            >
              <IconSolid name={copied ? 'check' : 'copy'} size="sm" />
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Close modal"
            >
              <IconSolid name="times" size="lg" className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Description */}
            {description && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 p-4 rounded-r-lg">
                <p className="text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </div>
            )}

            {/* Tags */}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Prompt Content */}
            <div className="bg-slate-900 text-cyan-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
              <pre className="whitespace-pre-wrap leading-relaxed">{prompt}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PromptModal
